import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { Order, MenuItem } from '../types';
import { apiService } from '../services/apiService';
import { formatEuro, formatDateTime } from '../utils/format';
import {
  History,
  X,
  Search,
  Phone,
  Calendar,
  ShoppingBag,
  CheckCircle2,
  Clock,
  Flame,
  Award,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Utensils,
  Plus,
  Sparkles,
  User
} from 'lucide-react';

interface CustomerOrderHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CustomerOrderHistoryModal: React.FC<CustomerOrderHistoryModalProps> = ({
  isOpen,
  onClose
}) => {
  const { customerProfile, categories, addToCart, setIsCartDrawerOpen } = useApp();
  const { lang, t } = useLanguage();

  const [phoneInput, setPhoneInput] = useState(customerProfile?.phone || '');
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [reorderSuccessId, setReorderSuccessId] = useState<string | null>(null);

  const fetchOrdersForPhone = async (phoneToQuery: string) => {
    if (!phoneToQuery.trim()) return;
    setIsLoading(true);
    setHasSearched(true);
    try {
      const results = await apiService.getCustomerOrdersByPhone(phoneToQuery.trim());
      setOrders(results || []);
      if (results && results.length > 0) {
        setExpandedOrderId(results[0].id);
      }
    } catch (e) {
      console.error('Failed to fetch past orders:', e);
      setOrders([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (customerProfile?.phone) {
        setPhoneInput(customerProfile.phone);
        fetchOrdersForPhone(customerProfile.phone);
      } else {
        setOrders([]);
        setHasSearched(false);
      }
    }
  }, [isOpen, customerProfile?.phone]);

  if (!isOpen) return null;

  const totalSpent = orders.reduce((sum, o) => sum + (Number(o.total_amount) || 0), 0);
  const totalOrders = orders.length;

  const toggleExpandOrder = (orderId: string) => {
    setExpandedOrderId(prev => (prev === orderId ? null : orderId));
  };

  const handleReorder = (order: Order) => {
    const allMenuItems = categories.flatMap(c => c.items || []);
    order.items.forEach(orderItem => {
      const found = allMenuItems.find(
        m =>
          m.name_fi.toLowerCase() === orderItem.name.toLowerCase() ||
          m.name_en.toLowerCase() === orderItem.name.toLowerCase() ||
          m.name_sv.toLowerCase() === orderItem.name.toLowerCase() ||
          m.id === orderItem.id
      );

      const itemToAdd: MenuItem = found || {
        id: orderItem.id || `item_${Date.now()}`,
        category_id: 'cat_pizza',
        name_fi: orderItem.name,
        name_en: orderItem.name,
        name_sv: orderItem.name,
        description_fi: '',
        description_en: '',
        description_sv: '',
        price: orderItem.price || 0,
        vat_rate: 14,
        image_url: '',
        is_available: true,
        spice_level: 0,
        dietary_flags: [],
        sort_order: 1
      };

      addToCart(
        itemToAdd,
        orderItem.quantity || 1,
        orderItem.selected_variants || [],
        orderItem.special_instructions
      );
    });

    setReorderSuccessId(order.id);
    setTimeout(() => {
      setReorderSuccessId(null);
      onClose();
      setIsCartDrawerOpen(true);
    }, 600);
  };

  const getStatusPill = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span
            style={{
              fontSize: '0.72rem',
              fontWeight: 700,
              padding: '3px 8px',
              borderRadius: 'var(--radius-full)',
              background: 'rgba(16, 185, 129, 0.12)',
              color: 'var(--accent-emerald)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <CheckCircle2 size={11} />
            <span>{lang === 'en' ? 'Completed' : 'Valmis'}</span>
          </span>
        );
      case 'ready':
        return (
          <span
            style={{
              fontSize: '0.72rem',
              fontWeight: 700,
              padding: '3px 8px',
              borderRadius: 'var(--radius-full)',
              background: 'rgba(56, 189, 248, 0.12)',
              color: '#38bdf8',
              border: '1px solid rgba(56, 189, 248, 0.3)'
            }}
          >
            {lang === 'en' ? 'Ready to Serve' : 'Tarjoiltu'}
          </span>
        );
      case 'preparing':
        return (
          <span
            style={{
              fontSize: '0.72rem',
              fontWeight: 700,
              padding: '3px 8px',
              borderRadius: 'var(--radius-full)',
              background: 'rgba(224, 90, 54, 0.12)',
              color: 'var(--accent-copper)',
              border: '1px solid rgba(224, 90, 54, 0.3)'
            }}
          >
            {lang === 'en' ? 'In Oven' : 'Uunissa'}
          </span>
        );
      case 'confirmed':
        return (
          <span
            style={{
              fontSize: '0.72rem',
              fontWeight: 700,
              padding: '3px 8px',
              borderRadius: 'var(--radius-full)',
              background: 'rgba(245, 158, 11, 0.12)',
              color: 'var(--accent-amber)',
              border: '1px solid rgba(245, 158, 11, 0.3)'
            }}
          >
            {lang === 'en' ? 'Confirmed' : 'Vahvistettu'}
          </span>
        );
      case 'cancelled':
        return (
          <span
            style={{
              fontSize: '0.72rem',
              fontWeight: 700,
              padding: '3px 8px',
              borderRadius: 'var(--radius-full)',
              background: 'rgba(239, 68, 68, 0.1)',
              color: 'var(--accent-rose)',
              border: '1px solid rgba(239, 68, 68, 0.25)'
            }}
          >
            {lang === 'en' ? 'Cancelled' : 'Peruttu'}
          </span>
        );
      default:
        return (
          <span
            style={{
              fontSize: '0.72rem',
              fontWeight: 700,
              padding: '3px 8px',
              borderRadius: 'var(--radius-full)',
              background: 'rgba(245, 158, 11, 0.12)',
              color: 'var(--accent-amber)',
              border: '1px solid rgba(245, 158, 11, 0.3)'
            }}
          >
            {lang === 'en' ? 'Received' : 'Vastaanotettu'}
          </span>
        );
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        zIndex: 125,
        backdropFilter: 'blur(8px)',
        background: 'rgba(10, 12, 16, 0.72)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px'
      }}
    >
      <div
        className="modal-card"
        onClick={e => e.stopPropagation()}
        style={{
          maxWidth: '620px',
          width: '100%',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          overflow: 'hidden',
          borderRadius: 'var(--radius-xl)',
          background: 'var(--bg-card)',
          border: '1px solid var(--border-medium)',
          boxShadow: '0 20px 45px rgba(0, 0, 0, 0.35)'
        }}
      >
        {/* Sleek Minimal Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--border-subtle)',
            background: 'var(--bg-surface)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: 'var(--radius-md)',
                background: 'rgba(224, 90, 54, 0.12)',
                color: 'var(--accent-copper)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <History size={18} />
            </div>
            <div>
              <div style={{ fontSize: '1.05rem', fontWeight: 800, fontFamily: 'var(--font-heading)', color: 'var(--text-primary)' }}>
                {lang === 'en' ? 'My Order History' : 'Oma Tilaushistoria'}
              </div>
              <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>
                theovenstory.fi • Helsinki
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            className="modal-close-btn"
            style={{ width: '32px', height: '32px' }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Minimal Search by Phone */}
        <div style={{ padding: '14px 20px', background: 'var(--bg-primary)', borderBottom: '1px solid var(--border-subtle)' }}>
          <form
            onSubmit={e => {
              e.preventDefault();
              fetchOrdersForPhone(phoneInput);
            }}
            style={{ display: 'flex', gap: '8px' }}
          >
            <div style={{ position: 'relative', flex: 1 }}>
              <Phone
                size={15}
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)'
                }}
              />
              <input
                type="tel"
                className="form-input"
                placeholder="+358 40 123 4567"
                value={phoneInput}
                onChange={e => setPhoneInput(e.target.value)}
                style={{
                  paddingLeft: '36px',
                  height: '38px',
                  fontSize: '0.88rem',
                  borderRadius: 'var(--radius-md)'
                }}
              />
            </div>
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading || !phoneInput.trim()}
              style={{
                height: '38px',
                padding: '0 14px',
                fontSize: '0.82rem',
                gap: '6px',
                borderRadius: 'var(--radius-md)'
              }}
            >
              {isLoading ? <RefreshCw size={14} className="anim-spin" /> : <Search size={14} />}
              <span>{lang === 'en' ? 'Search' : 'Hae'}</span>
            </button>
          </form>

          {/* Minimal Customer Profile Ribbon */}
          {hasSearched && totalOrders > 0 && (
            <div
              style={{
                marginTop: '10px',
                padding: '8px 12px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-subtle)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '6px',
                fontSize: '0.8rem'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: 'var(--accent-copper)',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    fontSize: '0.72rem'
                  }}
                >
                  {(orders[0]?.customer_name || 'A')[0].toUpperCase()}
                </div>
                <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                  {orders[0]?.customer_name || 'Asiakas'}
                </span>
                <span style={{ color: 'var(--text-muted)' }}>•</span>
                <span style={{ color: 'var(--accent-copper)', fontWeight: 700 }}>
                  {totalOrders} {totalOrders === 1 ? (lang === 'en' ? 'order' : 'tilaus') : (lang === 'en' ? 'orders' : 'tilausta')}
                </span>
              </div>

              <div style={{ fontWeight: 800, color: 'var(--text-primary)', fontSize: '0.82rem' }}>
                <span style={{ color: 'var(--text-muted)', fontWeight: 500, marginRight: '4px' }}>
                  {lang === 'en' ? 'Total spent:' : 'Yhteensä:'}
                </span>
                {formatEuro(totalSpent)}
              </div>
            </div>
          )}
        </div>

        {/* Minimal Scrollable Orders List */}
        <div
          style={{
            padding: '16px 20px',
            overflowY: 'auto',
            flex: '1 1 auto',
            minHeight: 0,
            maxHeight: 'calc(85vh - 150px)',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '36px 0', color: 'var(--text-muted)' }}>
              <RefreshCw size={24} className="anim-spin" style={{ margin: '0 auto 10px', color: 'var(--accent-copper)' }} />
              <div style={{ fontSize: '0.85rem' }}>{lang === 'en' ? 'Loading orders...' : 'Ladataan tilauksia...'}</div>
            </div>
          ) : !hasSearched ? (
            <div style={{ textAlign: 'center', padding: '36px 20px', color: 'var(--text-muted)' }}>
              <ShoppingBag size={32} style={{ margin: '0 auto 8px', opacity: 0.4 }} />
              <p style={{ margin: 0, fontSize: '0.85rem' }}>
                {lang === 'en'
                  ? 'Enter your mobile number to view past orders.'
                  : 'Syötä puhelinnumerosi nähdäksesi aiemmat tilauksesi.'}
              </p>
            </div>
          ) : orders.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '36px 20px', color: 'var(--text-muted)' }}>
              <Utensils size={32} style={{ margin: '0 auto 8px', opacity: 0.4 }} />
              <p style={{ margin: '0 0 4px', fontWeight: 700, color: 'var(--text-primary)', fontSize: '0.88rem' }}>
                {lang === 'en' ? 'No orders found' : 'Ei tilauksia'}
              </p>
              <p style={{ margin: 0, fontSize: '0.78rem' }}>
                {lang === 'en'
                  ? 'Orders made with this number will appear here automatically.'
                  : 'Tällä numerolla tehdyt tilaukset näkyvät täällä automaattisesti.'}
              </p>
            </div>
          ) : (
            orders.map(order => {
              const isExpanded = expandedOrderId === order.id;
              const previewText = (order.items || [])
                .map(it => `${it.quantity}x ${it.name}`)
                .join(', ');

              return (
                <div
                  key={order.id}
                  style={{
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: 'var(--radius-md)',
                    overflow: 'hidden',
                    transition: 'border-color 0.2s ease'
                  }}
                >
                  {/* Order Accordion Card Header */}
                  <div
                    onClick={() => toggleExpandOrder(order.id)}
                    style={{
                      padding: '12px 14px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '10px',
                      userSelect: 'none'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', minWidth: 0, flex: 1 }}>
                      <span
                        style={{
                          fontWeight: 800,
                          fontSize: '0.86rem',
                          color: 'var(--accent-copper)',
                          background: 'rgba(224, 90, 54, 0.1)',
                          padding: '2px 6px',
                          borderRadius: 'var(--radius-xs)'
                        }}
                      >
                        {order.order_number}
                      </span>

                      <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)' }}>
                        Pöytä #{order.table_number}
                      </span>

                      {getStatusPill(order.status)}

                      <span style={{ fontSize: '0.74rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <Clock size={11} />
                        <span>{formatDateTime(order.created_at)}</span>
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                      <span style={{ fontWeight: 800, fontSize: '0.92rem', color: 'var(--text-primary)' }}>
                        {formatEuro(order.total_amount)}
                      </span>
                      <div style={{ color: 'var(--text-muted)' }}>
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </div>
                    </div>
                  </div>

                  {/* Collapsed Dish Preview */}
                  {!isExpanded && (
                    <div
                      onClick={() => toggleExpandOrder(order.id)}
                      style={{
                        padding: '0 14px 10px',
                        fontSize: '0.76rem',
                        color: 'var(--text-muted)',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {previewText}
                    </div>
                  )}

                  {/* Expanded Itemized Breakdown & 1-Click Reorder */}
                  {isExpanded && (
                    <div
                      style={{
                        padding: '10px 14px 12px',
                        borderTop: '1px solid var(--border-subtle)',
                        background: 'var(--bg-primary)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '5px',
                          maxHeight: '220px',
                          overflowY: 'auto',
                          paddingRight: '4px'
                        }}
                      >
                        {(order.items || []).map((item, idx) => (
                          <div
                            key={idx}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              fontSize: '0.82rem'
                            }}
                          >
                            <div style={{ minWidth: 0, flex: 1, paddingRight: '8px' }}>
                              <span style={{ fontWeight: 800, color: 'var(--accent-copper)', marginRight: '5px' }}>
                                {item.quantity}x
                              </span>
                              <span style={{ color: 'var(--text-primary)' }}>{item.name}</span>
                              {item.selected_variants && item.selected_variants.length > 0 && (
                                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginLeft: '18px' }}>
                                  {item.selected_variants.map((v: any) => v.option_name || v.name).join(', ') || ''}
                                </div>
                              )}
                            </div>
                            <span style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                              {formatEuro((item.price || 0) * (item.quantity || 1))}
                            </span>
                          </div>
                        ))}
                      </div>

                      {order.special_notes && (
                        <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', fontStyle: 'italic', paddingTop: '4px' }}>
                          "{order.special_notes}"
                        </div>
                      )}

                      {/* Reorder Action Bar */}
                      <div
                        style={{
                          borderTop: '1px dashed var(--border-subtle)',
                          paddingTop: '8px',
                          marginTop: '4px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: '8px'
                        }}
                      >
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                          {order.items?.length || 0} {lang === 'en' ? 'items' : 'tuotetta'} • ALV sis.
                        </div>

                        <button
                          type="button"
                          onClick={() => handleReorder(order)}
                          style={{
                            background: reorderSuccessId === order.id ? 'var(--accent-emerald)' : 'rgba(224, 90, 54, 0.1)',
                            border: `1px solid ${reorderSuccessId === order.id ? 'var(--accent-emerald)' : 'var(--accent-copper)'}`,
                            color: reorderSuccessId === order.id ? '#ffffff' : 'var(--accent-copper)',
                            padding: '5px 12px',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <Sparkles size={13} />
                          <span>
                            {reorderSuccessId === order.id
                              ? (lang === 'en' ? 'Added to Cart!' : 'Lisätty ostoskoriin!')
                              : (lang === 'en' ? 'Reorder Items' : 'Tilaa nämä uudelleen')}
                          </span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Minimal Footer */}
        <div
          style={{
            padding: '10px 20px',
            background: 'var(--bg-surface)',
            borderTop: '1px solid var(--border-subtle)',
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <button
            className="btn-secondary"
            onClick={onClose}
            style={{ padding: '6px 16px', fontSize: '0.82rem', borderRadius: 'var(--radius-md)' }}
          >
            {lang === 'en' ? 'Close' : 'Sulje'}
          </button>
        </div>
      </div>
    </div>
  );
};
